// third-party
import { createSlice } from "@reduxjs/toolkit";

// project imports
import axios from "axios"; // this does not include app headers
import axiosServices from "../../utils/axios";
import { dispatch } from "../index";
import { storage } from "../../utils/helpers/storage";
import { SET_SELECTED_CLIENT } from "../actions";
import { jwtDecode } from "jwt-decode";
import {
  filterVisibleClients,
  filterVisibleSites,
} from "../../utils/helpers/permissions";

// ----------------------------------------------------------------------

const initialState = {
  error: null,
  customers: { count: 0, rows: [] },
  selectedClient: null,
  selectedSiteConfig: null,
};

const slice = createSlice({
  name: "customer",
  initialState,
  reducers: {
    // HAS ERROR
    hasError(state, action) {
      state.error = action.payload;
    },

    // GET CUSTOMERS
    getCustomersSuccess(state, action) {
      state.customers = action.payload;
    },

    // SET SELECTED User
    getSelectedClientSuccess(state, action) {
      if (action.payload) {
        const existingDomain = storage.getDomain();
        const isLocusUser = storage.getLocus();
        // Reject update if non-locus user tries to load a foreign client
        const shouldUpdate =
          isLocusUser || !existingDomain || existingDomain === action.payload?.domain;

        if (!shouldUpdate) {
          return;
        }

        storage.setRegion(action.payload?.region || "eus");
        storage.setDomain(action.payload?.domain || "");
        storage.setClientId(action.payload?.id || "");
        storage.setSSOIdpUrl(action.payload?.ssoIdpUrl || "");
        storage.setSSOIdpClient(action.payload?.ssoIdpClient || "");

        axiosServices.defaults.headers["locus-domain"] = action.payload?.domain;
        axiosServices.defaults.headers["locus-region"] =
          action.payload?.region || "eus";
      }
      let _sites = [];

      if (action?.payload?.sites) {
        // Sites may be full objects or plain ID strings (find-by-domain returns strings).
        const isStringArray = action.payload.sites.length > 0 && typeof action.payload.sites[0] === "string";

        if (isStringArray) {
          // Keep string IDs as-is; no sorting or filtering possible without metadata
          _sites = action.payload.sites;
          action.payload.sites = _sites;
        } else {
          _sites = action?.payload?.sites
            .filter((site) => site.status !== "Decommissioned")
            .sort((a, b) => a.friendlyName.localeCompare(b.friendlyName));

          let decommisionedSites = action?.payload?.sites
            .filter((site) => site.status === "Decommissioned")
            .sort((a, b) => a.friendlyName.localeCompare(b.friendlyName));

          action.payload.sites = _sites.concat(decommisionedSites);
        }
      }
      state.selectedClient = action.payload;
    },
    setSelectedSiteConfig(state, action) {
      state.selectedSiteConfig = action.payload;
    },
  },
});

// Reducer
export default slice.reducer;

// ----------------------------------------------------------------------

export function getCustomers({
  pageNumber,
  pageSize,
  sortField,
  sortDirection,
  searchFilter,
}) {
  return async (_, getState) => {
    let params = {};
    if (pageNumber) {
      params.page = pageNumber;
    }
    if (pageSize) {
      params.pageSize = pageSize;
    }
    if (sortField) {
      params.sort = sortField;
    }
    if (sortDirection) {
      params.sortDirection = sortDirection.toUpperCase(); //Available values : ASC, DESC
    }
    if (searchFilter) {
      // params.filter = searchFilter;
      params.name = searchFilter;
    }
    try {
      const decoded = storage.getAccessToken()
        ? jwtDecode(storage.getAccessToken())
        : null;
      const response = await axiosServices.get(
        `${process.env.REACT_APP_SERVICES_API}/services/client`,
        {
          params,
          headers: {
            "locus-domain": decoded?.domain,
            "locus-region": storage.getUserRegion()
              ? storage.getUserRegion()
              : storage.getRegion(),
          },
        },
      );
      const activeUser = getState()?.user?.activeUser;
      response.data.rows = filterVisibleClients(response.data?.rows, activeUser);
      response.data.rows.forEach((client) => {
        client.sites = filterVisibleSites(client?.sites, activeUser);
      });
      dispatch(slice.actions.getCustomersSuccess(response.data));
    } catch (error) {
      dispatch(
        slice.actions.getCustomersSuccess({
          count: 0,
          rows: [],
        }),
      );
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getCustomer(id) {
  return async (_, getState) => {
    try {
      const response = await axiosServices.get(
        `${process.env.REACT_APP_SERVICES_API}/services/client/${id}`,
      );
      const activeUser = getState()?.user?.activeUser;
      response.data.sites = filterVisibleSites(response.data?.sites, activeUser);
      dispatch(slice.actions.getSelectedClientSuccess(response.data));
      return response.data;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return error;
    }
  };
}

export function findClient(domain) {
  domain = domain ? domain : storage.getDomain();
  return async (_, getState) => {
    try {
      const response = await axiosServices.get(
        `${process.env.REACT_APP_SERVICES_API}/services/client/find?domain=${domain}`,
      );
      const activeUser = getState()?.user?.activeUser;
      response.data.sites = filterVisibleSites(response.data?.sites, activeUser);
      dispatch(slice.actions.getSelectedClientSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return error;
    }
  };
}

export function getClientImageUploadToken(clientId, filename) {
  return async () => {
    try {
      return await axiosServices.get(
        `${process.env.REACT_APP_SERVICES_API}/services/client/${clientId}/upload`,
        {
          params: {
            filename: filename,
          },
        },
      );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return error;
    }
  };
}

export function uploadClientImage(token, file) {
  return async () => {
    try {
      return await axios.put(`${token.combinedUrl}`, file, {
        headers: {
          "Content-Type": file.type,
          "x-ms-blob-type": "BlockBlob",
          "x-ms-meta-contenttype": file.type,
          "x-ms-meta-originalfile": file.name,
          "x-ms-meta-filetype": file.type,
        },
      });
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return error;
    }
  };
}

export function createCustomer(data) {
  return async () => {
    try {
      return await axiosServices.post(
        `${process.env.REACT_APP_SERVICES_API}/services/client`,
        data,
        {
          headers: {
            // "locus-region-original": data.region,
            "locus-region-original": storage.getUserRegion(),
            "locus-region": data.region,
          },
        },
      );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return error;
    }
  };
}

export function updateCustomer(data) {
  return async () => {
    try {
      let clientId = data.id || null;
      delete data?.id;
      return await axiosServices.put(
        `${process.env.REACT_APP_SERVICES_API}/services/client/${clientId}`,
        data,
        {},
      );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return error;
    }
  };
}

export function addSiteToClient(data, id) {
  return async () => {
    try {
      return await axiosServices.patch(
        `${process.env.REACT_APP_SERVICES_API}/services/client/${id}/append`,
        data,
        {},
      );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return error;
    }
  };
}

export function createSite(data) {
  return async () => {
    try {
      return await axiosServices.post(
        `${process.env.REACT_APP_SERVICES_API}/services/site`,
        data,
        {
          headers: {
            "locus-region-original": storage.getUserRegion(),
            "locus-domain": "locus",
          },
        },
      );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return error;
    }
  };
}

export function setSelectedClient(customer) {
  return async (_, getState) => {
    try {
      const activeUser = getState()?.user?.activeUser;
      if (customer) {
        customer.sites = filterVisibleSites(customer?.sites, activeUser);
      }
      dispatch({
        type: SET_SELECTED_CLIENT,
        payload: customer,
      });
      dispatch(slice.actions.getSelectedClientSuccess(customer));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export async function decommissionSite(id) {
  return await axiosServices.delete(
    `${process.env.REACT_APP_SERVICES_API}/services/site/${id}`,
  );
}

export function getSiteConfig(siteId) {
  return async (dispatch) => {
    let params = {};
    if (siteId) {
      params.site = siteId;
    }
    try {
      const response = await axiosServices.get(
        `${process.env.REACT_APP_API}/config/config/target`,
        {
          params,
        },
      );
      dispatch(
        slice.actions.setSelectedSiteConfig(
          response?.data && response?.data?.rows[0]
            ? response?.data?.rows[0]
            : null,
        ),
      );
      return response.data;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function updateSiteConfig(targetId, params) {
  return async (dispatch) => {
    try {
      const response = await axiosServices.put(
        `${process.env.REACT_APP_API}/config/config/target/${targetId}`,
        params,
      );
      dispatch(slice.actions.setSelectedSiteConfig(response.data));
      return response.data;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
